/**
 * "Telefonla giriş yap" — the television's side of pairing.
 *
 * ## Why this screen exists at all
 *
 * Typing an email address and a password with a remote is the worst interaction in the
 * app: four arrow presses per character, on a keyboard laid out for neither. This replaces
 * it with reading six characters off the screen, and the password never travels to the
 * television — which matters for a device that is more likely than any other in the house
 * to be sold or repaired with its storage intact.
 *
 * ## The QR and the code are not alternatives
 *
 * Both are always shown. A QR fails in ordinary rooms — a bright panel, a camera that will
 * not focus at that distance, a phone whose scanner opens the wrong app — and when it does,
 * the answer has to be on the same screen rather than behind a "having trouble?" link the
 * viewer has to find with a remote.
 *
 * ## Polling, with the socket as an optimisation
 *
 * The panel has no token yet, so it cannot join the realtime channel — it polls. The
 * `pairing:approved` event exists for the case where it *is* already connected (a
 * re-pair), and it only saves a few seconds of waiting. Pairing completes through the claim
 * call either way, which is what keeps the socket out of the credential path.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PairingStartDto, UserDto } from '@filmodasi/shared';
import { PAIRING_TTL_MINUTES } from '@filmodasi/shared';
import { api, apiUrl, deviceId, packagedHeaders, publicOrigin } from '../../../api/client';
import { useSession } from '../../../store/session';
import { ErrorBox, Spinner } from '../../../components/ui';
import { useFocusable } from '../focus/useFocusable';
import { platformLabel } from '../../../realtime/useRealtime';

/** Slow enough to be polite to the server, fast enough that nobody watches a dead screen. */
const POLL_MS = 3_000;

/**
 * Where the phone should land, or null when no absolute origin is available.
 *
 * This used to read `apiUrl('')`, which returns the empty string — `apiUrl` only
 * substitutes the base for paths beginning with `/`. So the fallback always won, and on
 * the packaged widget that fallback is `file://`: every television drew a QR encoding
 * `file:///pair?code=…`, which scans cleanly and opens nothing.
 */
function pairUrl(code: string): string | null {
  const origin = publicOrigin();
  return origin && `${origin}/pair?code=${encodeURIComponent(code)}`;
}

export function TvPairPage() {
  const { adoptSession } = useSession();
  const navigate = useNavigate();
  const [pairing, setPairing] = useState<PairingStartDto | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  // The secret must not go into React state that anything else can read; it is held here
  // and used exactly once, by the claim call below.
  const secretRef = useRef<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const start = useCallback(async () => {
    setError(null);
    setExpired(false);
    setQr(null);
    try {
      const started = await api<PairingStartDto>('/api/devices/pair/start', {
        method: 'POST',
        withoutProfile: true,
        body: {
          deviceId: deviceId(),
          name: 'Salon TV',
          // Shown to the person approving on their phone, so it has to be recognisable
          // rather than complete — see `platformLabel`.
          platform: platformLabel(),
        },
      });
      secretRef.current = started.secret;
      // Stored without the secret: nothing that renders should be able to reach it.
      setPairing({ ...started, secret: '' });

      /*
       * Rendered locally rather than fetched from a QR service — the widget runs from
       * file:// with no network origin, and a pairing code has no business leaving the
       * household to be drawn.
       *
       * Imported dynamically, for the same reason the 3D layer is: this is the only screen
       * in the app that draws a QR, and it is a screen a phone never reaches. A static
       * import put the encoder in the entry chunk of every device — thirty-odd kilobytes
       * that a phone downloads on every visit and never executes.
       */
      const url = pairUrl(started.code);
      if (url) {
        const { default: QRCode } = await import('qrcode');
        setQr(
          await QRCode.toDataURL(url, {
            width: 320,
            margin: 1,
            errorCorrectionLevel: 'M',
          }),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eşleştirme başlatılamadı');
    }
  }, []);

  useEffect(() => {
    void start();
  }, [start]);

  /* -- poll for the claim -- */
  useEffect(() => {
    if (!pairing || expired) return;

    let cancelled = false;

    const attempt = async () => {
      const secret = secretRef.current;
      if (!secret || cancelled) return;

      try {
        /*
         * Deliberately not through `api()`: this is the packaged-Tizen credential
         * exchange, and it must carry the widget headers and take the refresh token from
         * the body. Routing it through the ordinary client would attach the profile
         * headers and the 401-refresh cycle, neither of which applies before a session
         * exists.
         */
        const res = await fetch(apiUrl('/api/devices/pair/claim'), {
          method: 'POST',
          // `include` matters for the non-packaged path: the server delivers the refresh
          // token as a Secure httpOnly cookie there, and without this the response would
          // set a cookie the browser discards.
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...packagedHeaders() },
          body: JSON.stringify({
            code: pairing.code,
            secret,
            deviceId: deviceId(),
          }),
        });

        if (res.status === 200) {
          const data = (await res.json()) as {
            user: UserDto;
            accessToken: string;
            /** Packaged widget only; on the web the cookie is the delivery. */
            refreshToken?: string;
            profileId: string | null;
          };
          secretRef.current = null;
          cancelled = true;
          adoptSession(data);
          /*
           * Off this screen explicitly.
           *
           * Adopting the session is not enough: `/pair` belongs to the phone's approval
           * screen once a session exists, so a television that stayed here would finish
           * pairing and land on a page asking it to approve a television. `replace`, so
           * Back does not return to a pairing that has already been consumed.
           */
          void navigate('/home', { replace: true });
          return;
        }

        // 400 is the ordinary "not approved yet" answer — the same code every other
        // pairing failure gets, which is why this keeps polling rather than giving up.
        if (res.status === 403) {
          setError('Bu televizyonun erişimi kaldırılmış');
          cancelled = true;
        }
      } catch {
        // A dropped request mid-poll is not worth showing; the next tick retries.
      }
    };

    pollRef.current = window.setInterval(() => void attempt(), POLL_MS);
    return () => {
      cancelled = true;
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, [pairing, expired, adoptSession, navigate]);

  /* -- the code dies on screen; say so rather than letting it fail silently -- */
  useEffect(() => {
    if (!pairing) return;
    const remaining = new Date(pairing.expiresAt).getTime() - Date.now();
    const timer = window.setTimeout(() => setExpired(true), Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [pairing]);

  const { ref: retryRef, focused } = useFocusable<HTMLButtonElement>({
    id: 'pair:retry',
    autoFocus: true,
    onSelect: () => void start(),
  });

  return (
    <section className="tvpair">
      <h1 className="tvpair__title">Telefonla giriş yap</h1>

      {error && <ErrorBox message={error} />}
      {!pairing && !error && <Spinner label="Kod hazırlanıyor…" />}

      {pairing && !expired && (
        <div className="tvpair__body">
          <ol className="tvpair__steps">
            <li>Telefonunuzda Filmodasi'yi açın ve giriş yapın.</li>
            {/* No QR means no "okutun" — an instruction pointing at something that is not
                on screen is how somebody concludes the television is broken. */}
            <li>{qr ? 'Kodu okutun ya da elle girin.' : 'Aşağıdaki kodu girin.'}</li>
            <li>Hangi profille açılacağını seçip onaylayın.</li>
          </ol>

          <div className="tvpair__proof">
            {qr && <img className="tvpair__qr" src={qr} alt="" />}
            <div className="tvpair__code" aria-label="Eşleştirme kodu">
              {pairing.code.split('').map((ch, i) => (
                <span key={i}>{ch}</span>
              ))}
            </div>
            <p className="tvpair__ttl">Kod {PAIRING_TTL_MINUTES} dakika geçerli.</p>
          </div>
        </div>
      )}

      {expired && (
        <div className="tvpair__body">
          <p>Kodun süresi doldu.</p>
        </div>
      )}

      <button
        ref={retryRef}
        type="button"
        className={`tvpair__retry${focused ? ' is-focused' : ''}`}
        onClick={() => void start()}
      >
        Yeni kod al
      </button>
    </section>
  );
}

export default TvPairPage;
